import * as i2 from '@angular/common';
import { DOCUMENT, CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { InjectionToken, Component, ChangeDetectionStrategy, ViewEncapsulation, Optional, Inject, HostBinding, Input, Injectable, NgModule } from '@angular/core';
import { mixinColor } from '@paysafe-ui/components/core';
import { ANIMATION_MODULE_TYPE } from '@angular/platform-browser/animations';
import { coerceNumberProperty } from '@angular/cdk/coercion';
import * as i1 from '@angular/cdk/platform';
import * as i1$1 from '@angular/cdk/overlay';
import { OverlayConfig, OverlayModule } from '@angular/cdk/overlay';
import { ComponentPortal, PortalInjector } from '@angular/cdk/portal';

/**
 * Base reference size of the spinner.
 * @docs-private
 */
const BASE_SIZE = 100;
/**
 * Base reference stroke width of the spinner.
 * @docs-private
 */
const BASE_STROKE_WIDTH = 10;
/**
 * Base reference stroke width of the determinate spinner.
 * @docs-private
 */
const BASE_DETERMINATE_STROKE_WIDTH = 8;
let nextUniqueId = 0;
const DEFAULT_MODE = 'indeterminate';
// Boilerplate for applying mixins to PsProgressSpinner.
/** @docs-private */
class PsProgressSpinnerBase {
    constructor(_elementRef) {
        this._elementRef = _elementRef;
    }
}
const _PsProgressSpinnerMixinBase = mixinColor(PsProgressSpinnerBase, 'primary');
/** Gets the shadow root of an element, if supported and the element is inside the Shadow DOM. */
const _getShadowRoot = (element, _document) => {
    // TODO(crisbeto): see whether we should move this into the CDK
    // feature detection utilities once #15616 gets merged in.
    if (typeof window !== 'undefined') {
        const { head } = _document;
        // Check whether the browser supports Shadow DOM.
        /* eslint-disable @typescript-eslint/unbound-method */
        if (head && (head.createShadowRoot || head.attachShadow)) {
            const rootNode = element.getRootNode ? element.getRootNode() : null;
            // We need to take the `ShadowRoot` off of `window`, because the built-in types are
            // incorrect. See https://github.com/Microsoft/TypeScript/issues/27929.
            if (rootNode instanceof window.ShadowRoot) {
                return rootNode;
            }
        }
    }
    return null;
};
/** @docs-private */
function PS_PROGRESS_SPINNER_DEFAULT_OPTIONS_FACTORY() {
    return { diameter: BASE_SIZE };
}
/** Injection token to be used to override the default options for `ps-progress-spinner`. */
const PS_PROGRESS_SPINNER_DEFAULT_OPTIONS = new InjectionToken('ps-progress-spinner-default-options', {
    providedIn: 'root',
    factory: PS_PROGRESS_SPINNER_DEFAULT_OPTIONS_FACTORY
});
// .0001 percentage difference is necessary in order to avoid unwanted animation frames
// for example because the animation duration is 4 seconds, .1% accounts to 4ms
// which are enough to see the flicker described in
// https://github.com/angular/components/issues/8984
const INDETERMINATE_ANIMATION_TEMPLATE = `
 @keyframes ps-progress-spinner-stroke-rotate-DIAMETER {
    0%      { stroke-dashoffset: START_VALUE;  transform: rotate(0); }
    12.5%   { stroke-dashoffset: END_VALUE;    transform: rotate(0); }
    12.5001%  { stroke-dashoffset: END_VALUE;    transform: rotateX(180deg) rotate(72.5deg); }
    25%     { stroke-dashoffset: START_VALUE;  transform: rotateX(180deg) rotate(72.5deg); }

    25.0001%   { stroke-dashoffset: START_VALUE;  transform: rotate(270deg); }
    37.5%   { stroke-dashoffset: END_VALUE;    transform: rotate(270deg); }
    37.5001%  { stroke-dashoffset: END_VALUE;    transform: rotateX(180deg) rotate(161.5deg); }
    50%     { stroke-dashoffset: START_VALUE;  transform: rotateX(180deg) rotate(161.5deg); }

    50.0001%  { stroke-dashoffset: START_VALUE;  transform: rotate(180deg); }
    62.5%   { stroke-dashoffset: END_VALUE;    transform: rotate(180deg); }
    62.5001%  { stroke-dashoffset: END_VALUE;    transform: rotateX(180deg) rotate(251.5deg); }
    75%     { stroke-dashoffset: START_VALUE;  transform: rotateX(180deg) rotate(251.5deg); }

    75.0001%  { stroke-dashoffset: START_VALUE;  transform: rotate(90deg); }
    87.5%   { stroke-dashoffset: END_VALUE;    transform: rotate(90deg); }
    87.5001%  { stroke-dashoffset: END_VALUE;    transform: rotateX(180deg) rotate(341.5deg); }
    100%    { stroke-dashoffset: START_VALUE;  transform: rotateX(180deg) rotate(341.5deg); }
  }
`;
/**
 * `<ps-progress-spinner>` component.
 */
class PsProgressSpinnerComponent extends _PsProgressSpinnerMixinBase {
    get psAnimationClass() {
        return this.noopAnimations;
    }
    get psSpinnerDiameterWidth() {
        return `${this.diameter}px`;
    }
    get psSpinnerDiameterHeight() {
        return `${this.diameter}px`;
    }
    /** The diameter of the progress spinner (will set width and height of svg). */
    get diameter() {
        return this._diameter;
    }
    set diameter(size) {
        this._diameter = coerceNumberProperty(size);
        // If this is set before `ngOnInit`, the style root may not have been resolved yet.
        if (!this._fallbackAnimation && this._styleRoot) {
            this._attachStyleNode();
        }
    }
    /** Stroke width of the progress spinner. */
    get strokeWidth() {
        return this._strokeWidth || this.getAdjustedStrokeWidth();
    }
    set strokeWidth(value) {
        this._strokeWidth = coerceNumberProperty(value);
    }
    get noopAnimations() {
        return this._noopAnimations;
    }
    /** The mode of the progress spinner. */
    get mode() {
        return this._mode;
    }
    set mode(value) {
        if (value === 'determinate') {
            // Due to product requirements, the default color for determinate mode is set to 'secondary'.
            // While color adjustments are possible, the order in which they are made is important.
            // If the color is set before the mode, it will be overridden.
            // However, if the color is set after the mode setup, the custom color will be applied.
            this.color = 'secondary';
        }
        this._mode = value;
    }
    /** Value of the progress bar. Defaults to zero. Mirrored to aria-valuenow. */
    get value() {
        return this.mode === 'determinate' ? this._value : 0;
    }
    set value(value) {
        this._value = Math.max(0, Math.min(100, coerceNumberProperty(value) || 0));
    }
    constructor(_elementRef, platform, _document, animationMode, defaults) {
        super(_elementRef);
        this._elementRef = _elementRef;
        this._document = _document;
        this._value = 0;
        this._uniqueId = `ps-progress-spinner-${(nextUniqueId += 1)}`;
        this.id = this._uniqueId;
        this._diameter = BASE_SIZE;
        this._fallbackAnimation = false;
        this._mode = DEFAULT_MODE;
        const trackedDiameters = PsProgressSpinnerComponent._diameters;
        // The base size is already inserted via the component's structural styles. We still
        // need to track it so we don't end up adding the same styles again.
        if (!trackedDiameters.has(_document.head)) {
            trackedDiameters.set(_document.head, new Set([BASE_SIZE]));
        }
        this._fallbackAnimation = platform.EDGE || platform.TRIDENT;
        this._noopAnimations =
            animationMode === 'NoopAnimations' && !!defaults && !defaults._forceAnimations;
        if (defaults) {
            if (defaults.diameter) {
                this.diameter = defaults.diameter;
            }
            if (defaults.strokeWidth) {
                this.strokeWidth = defaults.strokeWidth;
            }
        }
    }
    /** The radius of the spinner, adjusted for stroke width. */
    get _circleRadius() {
        return (this.diameter - this.baseStrokeWidth) / 2;
    }
    /** The view box of the spinner's svg element. */
    get _viewBox() {
        const viewBox = this._circleRadius * 2 + this.strokeWidth;
        return `0 0 ${viewBox} ${viewBox}`;
    }
    /** The stroke circumference of the svg circle. */
    get _strokeCircumference() {
        return this._circleRadius * 2 * Math.PI;
    }
    /** The dash offset of the svg circle. */
    get _strokeDashOffset() {
        // In fallback mode set the circle to 80% and rotate it with CSS.
        if (this._fallbackAnimation) {
            return this._strokeCircumference * 0.2;
        }
        if (this.mode === 'determinate') {
            return (this._strokeCircumference * (100 - this._value)) / 100;
        }
        return null;
    }
    /** Stroke width of the circle in percent. */
    get _circleStrokeWidth() {
        return (this.strokeWidth / this.diameter) * 100;
    }
    get _maxNgContentWidth() {
        return (this.diameter - this.strokeWidth) * 0.8;
    }
    get role() {
        return 'progressbar';
    }
    get psProgressSpinner() {
        return true;
    }
    get attrMode() {
        return this.mode;
    }
    get attrValueMin() {
        return 0;
    }
    get attrValueMax() {
        return 100;
    }
    get attrValueNow() {
        return this.value;
    }
    /**
     * Tracks diameters of existing instances to de-dupe generated styles (default d = 100).
     * We need to keep track of which elements the diameters were attached to, because for
     * elements in the Shadow DOM the style tags are attached to the shadow root, rather
     * than the document head.
     */
    static { this._diameters = new WeakMap(); }
    /* eslint-disable camelcase */
    static { this.ngAcceptInputType_diameter = 0; }
    static { this.ngAcceptInputType_strokeWidth = 0; }
    static { this.ngAcceptInputType_value = 0; }
    ngOnInit() {
        const element = this._elementRef.nativeElement;
        // Note that we need to look up the root node in ngOnInit, rather than the constructor, because
        // Angular seems to create the element outside the shadow root and then moves it inside, if the
        // node is inside an `ngIf` and a ShadowDom-encapsulated component.
        this._styleRoot = _getShadowRoot(element, this._document) || this._document.head;
        this._attachStyleNode();
        // On IE and Edge, we can't animate the `stroke-dashoffset`
        // reliably so we fall back to a non-spec animation.
        const animationClass = `ps-progress-spinner-indeterminate${this._fallbackAnimation ? '-fallback' : ''}-animation`;
        element.classList.add(animationClass);
    }
    /** Dynamically generates a style tag containing the correct animation for this diameter. */
    _attachStyleNode() {
        const styleRoot = this._styleRoot;
        const currentDiameter = this._diameter;
        const diameters = PsProgressSpinnerComponent._diameters;
        let diametersForElement = diameters.get(styleRoot);
        if (!diametersForElement || !diametersForElement.has(currentDiameter)) {
            const styleTag = this._document.createElement('style');
            styleTag.setAttribute('ps-spinner-animation', `${currentDiameter} `);
            styleTag.textContent = this._getAnimationText();
            styleRoot.appendChild(styleTag);
            if (!diametersForElement) {
                diametersForElement = new Set();
                diameters.set(styleRoot, diametersForElement);
            }
            diametersForElement.add(currentDiameter);
        }
    }
    /** Generates animation styles adjusted for the spinner's diameter. */
    _getAnimationText() {
        return (INDETERMINATE_ANIMATION_TEMPLATE
            // Animation should begin at 5% and end at 80%
            .replace(/START_VALUE/g, `${this._strokeCircumference * 0.95}`)
            .replace(/END_VALUE/g, `${this._strokeCircumference * 0.2}`)
            .replace(/DIAMETER/g, `${this.diameter}`));
    }
    get baseStrokeWidth() {
        return this.mode === 'determinate' ? BASE_DETERMINATE_STROKE_WIDTH : BASE_STROKE_WIDTH;
    }
    getAdjustedStrokeWidth() {
        const divisor = BASE_SIZE / this.baseStrokeWidth;
        return this.diameter / divisor;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsProgressSpinnerComponent, deps: [{ token: i0.ElementRef }, { token: i1.Platform }, { token: DOCUMENT, optional: true }, { token: ANIMATION_MODULE_TYPE, optional: true }, { token: PS_PROGRESS_SPINNER_DEFAULT_OPTIONS }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.12", type: PsProgressSpinnerComponent, selector: "ps-progress-spinner", inputs: { diameter: "diameter", strokeWidth: "strokeWidth", mode: "mode", value: "value", id: "id", color: "color" }, host: { properties: { "class._ps-animation-noopable": "this.psAnimationClass", "style.width": "this.psSpinnerDiameterWidth", "style.height": "this.psSpinnerDiameterHeight", "attr.role": "this.role", "class.ps-progress-spinner": "this.psProgressSpinner", "attr.mode": "this.attrMode", "attr.aria-valuemin": "this.attrValueMin", "attr.aria-valuemax": "this.attrValueMax", "attr.aria-valuenow": "this.attrValueNow", "attr.id": "this.id" } }, exportAs: ["psProgressSpinner"], usesInheritance: true, ngImport: i0, template: "<ng-container\n  *ngTemplateOutlet=\"mode === 'determinate' ? determinate : indeterminate\"\n></ng-container>\n\n<ng-template #indeterminate>\n  <!--\n  preserveAspectRatio of xMidYMid meet as the center of the viewport is the circle's\n  center. The center of the circle will remain at the center of the ps-progress-spinner\n  element containing the SVG. `focusable=\"false\"` prevents IE from allowing the user to\n  tab into the SVG element.\n-->\n\n  <svg\n    [style.width.px]=\"diameter\"\n    [style.height.px]=\"diameter\"\n    [attr.viewBox]=\"_viewBox\"\n    preserveAspectRatio=\"xMidYMid meet\"\n    focusable=\"false\"\n  >\n    <!--\n        Technically we can reuse the same `circle` element, however Safari has an issue that breaks\n        the SVG rendering in determinate mode, after switching between indeterminate and determinate.\n        Using a different element avoids the issue. An alternative to this is adding `display: none`\n        for a split second and then removing it when switching between modes, but it's hard to know\n        for how long to hide the element and it can cause the UI to blink.\n      -->\n    <circle\n      cx=\"50%\"\n      cy=\"50%\"\n      [attr.r]=\"_circleRadius\"\n      [style.animation-name]=\"'ps-progress-spinner-stroke-rotate-' + diameter\"\n      [style.stroke-dashoffset.px]=\"_strokeDashOffset\"\n      [style.stroke-dasharray.px]=\"_strokeCircumference\"\n      [style.stroke-width.%]=\"_circleStrokeWidth\"\n    ></circle>\n  </svg>\n</ng-template>\n\n<ng-template #determinate>\n  <svg\n    [style.width.px]=\"diameter\"\n    [style.height.px]=\"diameter\"\n    [attr.viewBox]=\"_viewBox\"\n    preserveAspectRatio=\"xMidYMid meet\"\n    focusable=\"false\"\n  >\n    <circle\n      [attr.r]=\"_circleRadius\"\n      [style.animation-name]=\"'ps-progress-spinner-stroke-rotate-' + diameter\"\n      [style.stroke-dashoffset.px]=\"_strokeDashOffset\"\n      [style.stroke-dasharray.px]=\"_strokeCircumference\"\n      [style.stroke-width.%]=\"_circleStrokeWidth\"\n      cx=\"50%\"\n      cy=\"50%\"\n    />\n  </svg>\n  <div class=\"determined-spinner-background\" [style.border-width.px]=\"strokeWidth\"></div>\n  <div\n    class=\"content\"\n    [style.max-width.px]=\"_maxNgContentWidth\"\n    [style.max-height.px]=\"_maxNgContentWidth\"\n  >\n    <ng-content></ng-content>\n  </div>\n</ng-template>\n", styles: [".ps-progress-spinner{display:block;position:relative}.ps-progress-spinner svg{position:absolute;transform:rotate(-90deg);top:0;left:0;transform-origin:center;overflow:visible}.ps-progress-spinner circle{fill:transparent;transform-origin:center;transition:stroke-dashoffset 225ms linear}._ps-animation-noopable.ps-progress-spinner circle{transition:none;animation:none}.ps-progress-spinner.ps-progress-spinner-indeterminate-animation[mode=indeterminate]{animation:ps-progress-spinner-linear-rotate 2s linear infinite}._ps-animation-noopable.ps-progress-spinner.ps-progress-spinner-indeterminate-animation[mode=indeterminate]{transition:none;animation:none}.ps-progress-spinner.ps-progress-spinner-indeterminate-animation[mode=indeterminate] circle{transition-property:stroke;animation-duration:4s;animation-timing-function:cubic-bezier(.35,0,.25,1);animation-iteration-count:infinite}._ps-animation-noopable.ps-progress-spinner.ps-progress-spinner-indeterminate-animation[mode=indeterminate] circle{transition:none;animation:none}.ps-progress-spinner.ps-progress-spinner-indeterminate-fallback-animation[mode=indeterminate]{animation:ps-progress-spinner-stroke-rotate-fallback 10s cubic-bezier(.87,.03,.33,1) infinite}._ps-animation-noopable.ps-progress-spinner.ps-progress-spinner-indeterminate-fallback-animation[mode=indeterminate]{transition:none;animation:none}.ps-progress-spinner.ps-progress-spinner-indeterminate-fallback-animation[mode=indeterminate] circle{transition-property:stroke}._ps-animation-noopable.ps-progress-spinner.ps-progress-spinner-indeterminate-fallback-animation[mode=indeterminate] circle{transition:none;animation:none}.ps-progress-spinner .determined-spinner-background{position:absolute;width:inherit;height:inherit;opacity:.1;border-radius:50%;border-style:solid}.ps-progress-spinner .content{display:flex;justify-content:center;align-items:center;position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);text-align:center;padding:30% .75rem;overflow:hidden;border-radius:50%}@keyframes ps-progress-spinner-linear-rotate{0%{transform:rotate(0)}to{transform:rotate(360deg)}}@keyframes ps-progress-spinner-stroke-rotate-100{0%{stroke-dashoffset:16.7878857234rem;transform:rotate(0)}12.5%{stroke-dashoffset:3.5342917312rem;transform:rotate(0)}12.5001%{stroke-dashoffset:3.5342917312rem;transform:rotateX(180deg) rotate(72.5deg)}25%{stroke-dashoffset:16.7878857234rem;transform:rotateX(180deg) rotate(72.5deg)}25.0001%{stroke-dashoffset:16.7878857234rem;transform:rotate(270deg)}37.5%{stroke-dashoffset:3.5342917312rem;transform:rotate(270deg)}37.5001%{stroke-dashoffset:3.5342917312rem;transform:rotateX(180deg) rotate(161.5deg)}50%{stroke-dashoffset:16.7878857234rem;transform:rotateX(180deg) rotate(161.5deg)}50.0001%{stroke-dashoffset:16.7878857234rem;transform:rotate(180deg)}62.5%{stroke-dashoffset:3.5342917312rem;transform:rotate(180deg)}62.5001%{stroke-dashoffset:3.5342917312rem;transform:rotateX(180deg) rotate(251.5deg)}75%{stroke-dashoffset:16.7878857234rem;transform:rotateX(180deg) rotate(251.5deg)}75.0001%{stroke-dashoffset:16.7878857234rem;transform:rotate(90deg)}87.5%{stroke-dashoffset:3.5342917312rem;transform:rotate(90deg)}87.5001%{stroke-dashoffset:3.5342917312rem;transform:rotateX(180deg) rotate(341.5deg)}to{stroke-dashoffset:16.7878857234rem;transform:rotateX(180deg) rotate(341.5deg)}}@keyframes ps-progress-spinner-stroke-rotate-fallback{0%{transform:rotate(0)}25%{transform:rotate(1170deg)}50%{transform:rotate(2340deg)}75%{transform:rotate(3510deg)}to{transform:rotate(4680deg)}}\n"], dependencies: [{ kind: "directive", type: i2.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsProgressSpinnerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ps-progress-spinner', exportAs: 'psProgressSpinner', changeDetection: ChangeDetectionStrategy.OnPush, encapsulation: ViewEncapsulation.None, template: "<ng-container\n  *ngTemplateOutlet=\"mode === 'determinate' ? determinate : indeterminate\"\n></ng-container>\n\n<ng-template #indeterminate>\n  <!--\n  preserveAspectRatio of xMidYMid meet as the center of the viewport is the circle's\n  center. The center of the circle will remain at the center of the ps-progress-spinner\n  element containing the SVG. `focusable=\"false\"` prevents IE from allowing the user to\n  tab into the SVG element.\n-->\n\n  <svg\n    [style.width.px]=\"diameter\"\n    [style.height.px]=\"diameter\"\n    [attr.viewBox]=\"_viewBox\"\n    preserveAspectRatio=\"xMidYMid meet\"\n    focusable=\"false\"\n  >\n    <!--\n        Technically we can reuse the same `circle` element, however Safari has an issue that breaks\n        the SVG rendering in determinate mode, after switching between indeterminate and determinate.\n        Using a different element avoids the issue. An alternative to this is adding `display: none`\n        for a split second and then removing it when switching between modes, but it's hard to know\n        for how long to hide the element and it can cause the UI to blink.\n      -->\n    <circle\n      cx=\"50%\"\n      cy=\"50%\"\n      [attr.r]=\"_circleRadius\"\n      [style.animation-name]=\"'ps-progress-spinner-stroke-rotate-' + diameter\"\n      [style.stroke-dashoffset.px]=\"_strokeDashOffset\"\n      [style.stroke-dasharray.px]=\"_strokeCircumference\"\n      [style.stroke-width.%]=\"_circleStrokeWidth\"\n    ></circle>\n  </svg>\n</ng-template>\n\n<ng-template #determinate>\n  <svg\n    [style.width.px]=\"diameter\"\n    [style.height.px]=\"diameter\"\n    [attr.viewBox]=\"_viewBox\"\n    preserveAspectRatio=\"xMidYMid meet\"\n    focusable=\"false\"\n  >\n    <circle\n      [attr.r]=\"_circleRadius\"\n      [style.animation-name]=\"'ps-progress-spinner-stroke-rotate-' + diameter\"\n      [style.stroke-dashoffset.px]=\"_strokeDashOffset\"\n      [style.stroke-dasharray.px]=\"_strokeCircumference\"\n      [style.stroke-width.%]=\"_circleStrokeWidth\"\n      cx=\"50%\"\n      cy=\"50%\"\n    />\n  </svg>\n  <div class=\"determined-spinner-background\" [style.border-width.px]=\"strokeWidth\"></div>\n  <div\n    class=\"content\"\n    [style.max-width.px]=\"_maxNgContentWidth\"\n    [style.max-height.px]=\"_maxNgContentWidth\"\n  >\n    <ng-content></ng-content>\n  </div>\n</ng-template>\n", styles: [".ps-progress-spinner{display:block;position:relative}.ps-progress-spinner svg{position:absolute;transform:rotate(-90deg);top:0;left:0;transform-origin:center;overflow:visible}.ps-progress-spinner circle{fill:transparent;transform-origin:center;transition:stroke-dashoffset 225ms linear}._ps-animation-noopable.ps-progress-spinner circle{transition:none;animation:none}.ps-progress-spinner.ps-progress-spinner-indeterminate-animation[mode=indeterminate]{animation:ps-progress-spinner-linear-rotate 2s linear infinite}._ps-animation-noopable.ps-progress-spinner.ps-progress-spinner-indeterminate-animation[mode=indeterminate]{transition:none;animation:none}.ps-progress-spinner.ps-progress-spinner-indeterminate-animation[mode=indeterminate] circle{transition-property:stroke;animation-duration:4s;animation-timing-function:cubic-bezier(.35,0,.25,1);animation-iteration-count:infinite}._ps-animation-noopable.ps-progress-spinner.ps-progress-spinner-indeterminate-animation[mode=indeterminate] circle{transition:none;animation:none}.ps-progress-spinner.ps-progress-spinner-indeterminate-fallback-animation[mode=indeterminate]{animation:ps-progress-spinner-stroke-rotate-fallback 10s cubic-bezier(.87,.03,.33,1) infinite}._ps-animation-noopable.ps-progress-spinner.ps-progress-spinner-indeterminate-fallback-animation[mode=indeterminate]{transition:none;animation:none}.ps-progress-spinner.ps-progress-spinner-indeterminate-fallback-animation[mode=indeterminate] circle{transition-property:stroke}._ps-animation-noopable.ps-progress-spinner.ps-progress-spinner-indeterminate-fallback-animation[mode=indeterminate] circle{transition:none;animation:none}.ps-progress-spinner .determined-spinner-background{position:absolute;width:inherit;height:inherit;opacity:.1;border-radius:50%;border-style:solid}.ps-progress-spinner .content{display:flex;justify-content:center;align-items:center;position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);text-align:center;padding:30% .75rem;overflow:hidden;border-radius:50%}@keyframes ps-progress-spinner-linear-rotate{0%{transform:rotate(0)}to{transform:rotate(360deg)}}@keyframes ps-progress-spinner-stroke-rotate-100{0%{stroke-dashoffset:16.7878857234rem;transform:rotate(0)}12.5%{stroke-dashoffset:3.5342917312rem;transform:rotate(0)}12.5001%{stroke-dashoffset:3.5342917312rem;transform:rotateX(180deg) rotate(72.5deg)}25%{stroke-dashoffset:16.7878857234rem;transform:rotateX(180deg) rotate(72.5deg)}25.0001%{stroke-dashoffset:16.7878857234rem;transform:rotate(270deg)}37.5%{stroke-dashoffset:3.5342917312rem;transform:rotate(270deg)}37.5001%{stroke-dashoffset:3.5342917312rem;transform:rotateX(180deg) rotate(161.5deg)}50%{stroke-dashoffset:16.7878857234rem;transform:rotateX(180deg) rotate(161.5deg)}50.0001%{stroke-dashoffset:16.7878857234rem;transform:rotate(180deg)}62.5%{stroke-dashoffset:3.5342917312rem;transform:rotate(180deg)}62.5001%{stroke-dashoffset:3.5342917312rem;transform:rotateX(180deg) rotate(251.5deg)}75%{stroke-dashoffset:16.7878857234rem;transform:rotateX(180deg) rotate(251.5deg)}75.0001%{stroke-dashoffset:16.7878857234rem;transform:rotate(90deg)}87.5%{stroke-dashoffset:3.5342917312rem;transform:rotate(90deg)}87.5001%{stroke-dashoffset:3.5342917312rem;transform:rotateX(180deg) rotate(341.5deg)}to{stroke-dashoffset:16.7878857234rem;transform:rotateX(180deg) rotate(341.5deg)}}@keyframes ps-progress-spinner-stroke-rotate-fallback{0%{transform:rotate(0)}25%{transform:rotate(1170deg)}50%{transform:rotate(2340deg)}75%{transform:rotate(3510deg)}to{transform:rotate(4680deg)}}\n"] }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i1.Platform }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [ANIMATION_MODULE_TYPE]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [PS_PROGRESS_SPINNER_DEFAULT_OPTIONS]
                }] }], propDecorators: { psAnimationClass: [{
                type: HostBinding,
                args: ['class._ps-animation-noopable']
            }], psSpinnerDiameterWidth: [{
                type: HostBinding,
                args: ['style.width']
            }], psSpinnerDiameterHeight: [{
                type: HostBinding,
                args: ['style.height']
            }], diameter: [{
                type: Input
            }], strokeWidth: [{
                type: Input
            }], mode: [{
                type: Input
            }], value: [{
                type: Input
            }], role: [{
                type: HostBinding,
                args: ['attr.role']
            }], psProgressSpinner: [{
                type: HostBinding,
                args: ['class.ps-progress-spinner']
            }], attrMode: [{
                type: HostBinding,
                args: ['attr.mode']
            }], attrValueMin: [{
                type: HostBinding,
                args: ['attr.aria-valuemin']
            }], attrValueMax: [{
                type: HostBinding,
                args: ['attr.aria-valuemax']
            }], attrValueNow: [{
                type: HostBinding,
                args: ['attr.aria-valuenow']
            }], id: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['attr.id']
            }], color: [{
                type: Input
            }] } });

class PsProgressSpinnerOverlayService {
    constructor(overlay, injector) {
        this.overlay = overlay;
        this.injector = injector;
        this.OVERLAY_MARGIN_TOP = '300px';
        this.BACKDROP_CLASS = 'ps-progress-spinner-backdrop';
    }
    /**
     * Method used to show progress spinner.
     * @param hasBackdrop - Optional parameter that can be used
     * for showing or hiding backdrop overlay.
     * By default it's set to TRUE.
     */
    showSpinner(hasBackdrop) {
        this.overlayRef = this.overlay.create(this.getOverlayConfig(hasBackdrop));
        return this.overlayRef.attach(new ComponentPortal(PsProgressSpinnerComponent, null, this.createInjector({ diameter: 48, strokeWidth: 4 })));
    }
    /** Method used to hide progress spinner. */
    hideSpinner() {
        if (!this.overlayRef) {
            return;
        }
        this.overlayRef.dispose();
    }
    getOverlayConfig(hasBackdrop = true) {
        return new OverlayConfig({
            hasBackdrop,
            positionStrategy: this.positionGloballyCenter(),
            backdropClass: this.BACKDROP_CLASS
        });
    }
    positionGloballyCenter() {
        return this.overlay.position().global().centerHorizontally().top(this.OVERLAY_MARGIN_TOP);
    }
    createInjector(data) {
        const injectorTokens = new WeakMap([[PS_PROGRESS_SPINNER_DEFAULT_OPTIONS, data]]);
        return new PortalInjector(this.injector, injectorTokens);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsProgressSpinnerOverlayService, deps: [{ token: i1$1.Overlay }, { token: i0.Injector }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsProgressSpinnerOverlayService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsProgressSpinnerOverlayService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: i1$1.Overlay }, { type: i0.Injector }] });

class PsProgressSpinnerOverlayModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsProgressSpinnerOverlayModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.12", ngImport: i0, type: PsProgressSpinnerOverlayModule, imports: [OverlayModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsProgressSpinnerOverlayModule, providers: [PsProgressSpinnerOverlayService], imports: [OverlayModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsProgressSpinnerOverlayModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [OverlayModule],
                    providers: [PsProgressSpinnerOverlayService]
                }]
        }] });

class PsProgressSpinnerModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsProgressSpinnerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.12", ngImport: i0, type: PsProgressSpinnerModule, declarations: [PsProgressSpinnerComponent], imports: [CommonModule, PsProgressSpinnerOverlayModule], exports: [PsProgressSpinnerComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsProgressSpinnerModule, imports: [CommonModule, PsProgressSpinnerOverlayModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsProgressSpinnerModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, PsProgressSpinnerOverlayModule],
                    exports: [PsProgressSpinnerComponent],
                    declarations: [PsProgressSpinnerComponent]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { PS_PROGRESS_SPINNER_DEFAULT_OPTIONS, PS_PROGRESS_SPINNER_DEFAULT_OPTIONS_FACTORY, PsProgressSpinnerComponent, PsProgressSpinnerModule, PsProgressSpinnerOverlayModule, PsProgressSpinnerOverlayService, _getShadowRoot };
